import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useParams, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import {
  Loader2, ArrowLeft, Mail, Phone, Smartphone, Building2, MapPin, Hash,
  ShieldCheck, Wallet, Package, ChevronDown, ChevronRight, Save, Send,
  UserCheck, UserX, Calendar, TrendingUp, Repeat, AlertCircle, FileText,
} from 'lucide-react';
import CategoryCRMPricingTree from '../components/admin/CategoryCRMPricingTree';

const TIER_BADGES = {
  end_user: { label: 'End user', color: 'bg-neutral-200 text-neutral-700' },
  builder: { label: 'Builder', color: 'bg-blue-100 text-blue-800' },
  retailer: { label: 'Retailer', color: 'bg-purple-100 text-purple-800' },
};

const monthName = (m) => new Date(2024, m - 1, 1).toLocaleString('en-GB', { month: 'short' });

const CustomerCRMProfile = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [inactiveThreshold, setInactiveThreshold] = useState(30);
  const [sendingReengage, setSendingReengage] = useState(false);
  const [reengageChannel, setReengageChannel] = useState('email');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers/${uid}/crm`);
      setProfile(r.data);
      const c = r.data.customer;
      setForm({
        name: c.name || '',
        company_name: c.company_name || '',
        vat_no: c.vat_no || '',
        eori_no: c.eori_no || '',
        postcode: c.postcode || '',
        address: c.address || '',
        contact_no: c.contact_no || '',
        mobile_no: c.mobile_no || '',
        user_type: c.user_type,
        credit_limit: c.credit_limit || 0,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load CRM profile');
    } finally { setLoading(false); }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  if (authLoading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  if (!currentUser) return <Navigate to="/login?redirect=/admin" replace />;
  if (!currentUser.is_admin) return <Navigate to="/" replace />;

  if (loading || !profile || !form) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>;
  }

  const c = profile.customer;
  const pending = !c.is_approved && c.requested_user_type;
  const daysSince = profile.days_since_last_order;
  const isInactive = daysSince != null && daysSince >= inactiveThreshold;
  const neverOrdered = profile.order_count === 0;
  const tier = TIER_BADGES[c.user_type] || TIER_BADGES.end_user;

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        company_name: form.company_name || null,
        vat_no: form.vat_no || null,
        eori_no: form.eori_no || null,
        postcode: form.postcode || null,
        address: form.address || null,
        contact_no: form.contact_no || null,
        mobile_no: form.mobile_no || null,
        user_type: form.user_type,
        credit_limit: Number(form.credit_limit) || 0,
      };
      await api.put(`/admin/customers/${c.id}`, payload);
      toast.success('Profile saved');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const approve = async () => {
    try {
      await api.put(`/admin/customers/${c.id}/approve`);
      toast.success('Trade tier approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const reject = async () => {
    if (!window.confirm('Reject this trade upgrade? The user stays as an end user.')) return;
    try {
      await api.put(`/admin/customers/${c.id}/reject`);
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const sendReengagement = async () => {
    setSendingReengage(true);
    try {
      const r = await api.post(`/admin/customers/${c.id}/reengage`, { channel: reengageChannel });
      const sentBits = Object.entries(r.data.sent || {}).filter(([_, v]) => v).map(([k]) => k);
      toast.success(sentBits.length ? `Nudge queued via ${sentBits.join(' + ')}` : 'Nudge attempted (see server logs)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setSendingReengage(false); }
  };

  const downloadQuote = async (fmt) => {
    try {
      const r = await api.get(`/admin/customers/${c.id}/quote-pdf`, {
        params: { format: fmt },
        responseType: 'blob',
      });
      const mime = fmt === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const blob = new Blob([r.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      const safe = (c.name || 'Customer').replace(/[^A-Za-z0-9 _-]/g, '').replace(/\s+/g, '_') || 'Customer';
      a.download = `Quotation-${safe}-${today}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Quotation ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Failed to build ${fmt.toUpperCase()} quotation`);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/admin')} className="text-xs text-neutral-600 hover:text-[#F46A25] flex items-center gap-1 mb-2">
          <ArrowLeft className="w-3 h-3" /> Back to customers
        </button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-[#12263A] flex items-center gap-3">
              {c.name}
              <span className={`text-xs font-bold uppercase px-2.5 py-1 rounded-sm ${tier.color}`} data-testid="crm-tier-badge">{tier.label}</span>
              {c.is_admin && <span className="text-xs font-bold uppercase px-2.5 py-1 rounded-sm bg-[#F46A25] text-white">Admin</span>}
              {pending && (
                <span className="text-xs font-bold uppercase px-2.5 py-1 rounded-sm bg-amber-100 text-amber-800 flex items-center gap-1" data-testid="crm-pending-badge">
                  <AlertCircle className="w-3 h-3" /> Pending: {c.requested_user_type}
                </span>
              )}
            </h1>
            <p className="text-sm text-neutral-600 mt-1 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {c.email}</span>
              {c.company_name && <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {c.company_name}</span>}
              {c.mobile_no && <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> {c.mobile_no}</span>}
            </p>
          </div>
          {pending && (
            <div className="flex items-center gap-2">
              <button onClick={approve} data-testid="crm-approve-btn" className="bg-green-600 hover:bg-green-700 text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-1">
                <UserCheck className="w-4 h-4" /> Approve trade tier
              </button>
              <button onClick={reject} data-testid="crm-reject-btn" className="bg-red-500 hover:bg-red-600 text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-1">
                <UserX className="w-4 h-4" /> Reject
              </button>
            </div>
          )}
          <div className="flex items-center gap-2" data-testid="crm-quote-actions">
            <button
              onClick={() => downloadQuote('pdf')}
              data-testid="crm-quote-pdf-btn"
              className="bg-[#12263A] hover:bg-black text-white font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-1"
              title="Download personalised quotation as PDF"
            >
              <FileText className="w-4 h-4" /> Quotation PDF
            </button>
            <button
              onClick={() => downloadQuote('docx')}
              data-testid="crm-quote-docx-btn"
              className="bg-white border border-[#12263A] hover:bg-neutral-100 text-[#12263A] font-bold text-sm px-4 py-2 rounded-sm flex items-center gap-1"
              title="Download personalised quotation as Word (.docx)"
            >
              <FileText className="w-4 h-4" /> Quotation Word
            </button>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatTile label="Total spent" value={`£${Number(profile.total_spent).toFixed(2)}`} icon={TrendingUp} accent testid="crm-stat-total-spent" />
        <StatTile label="Orders" value={profile.order_count} icon={Package} testid="crm-stat-orders" />
        <StatTile label="Avg order" value={`£${Number(profile.avg_order_value).toFixed(2)}`} icon={Calendar} testid="crm-stat-avg" />
        <StatTile
          label={profile.is_one_time_buyer ? '1-time buyer' : profile.is_repeat_buyer ? 'Repeat buyer' : 'No orders yet'}
          value={neverOrdered ? '—' : (profile.is_repeat_buyer ? `${profile.order_count}x` : 'Once')}
          icon={Repeat}
          testid="crm-stat-repeat"
        />
        <StatTile
          label="Last order"
          value={c.last_order_at ? new Date(c.last_order_at).toLocaleDateString('en-GB') : '—'}
          hint={daysSince != null ? `${daysSince} day${daysSince === 1 ? '' : 's'} ago` : 'Never ordered'}
          icon={Calendar}
          accent={isInactive || neverOrdered}
          testid="crm-stat-last"
        />
      </div>

      {/* Re-engagement */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="crm-reengagement-panel">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold uppercase text-[#12263A]">Inactive threshold</span>
          <input
            type="number"
            min="1"
            value={inactiveThreshold}
            onChange={e => setInactiveThreshold(Number(e.target.value) || 30)}
            data-testid="crm-inactive-threshold"
            className="w-20 border border-neutral-300 rounded-sm px-2 py-1 text-sm focus:outline-none focus:border-[#F46A25]"
          /> <span className="text-xs text-neutral-500">days</span>
        </div>
        {(isInactive || neverOrdered) ? (
          <div className="text-xs font-bold uppercase text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {neverOrdered ? 'Has never ordered' : `Inactive for ${daysSince} days`}
          </div>
        ) : (
          <div className="text-xs font-bold uppercase text-green-700">Active customer (last order within {inactiveThreshold}d)</div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={reengageChannel}
            onChange={e => setReengageChannel(e.target.value)}
            data-testid="crm-reengage-channel"
            className="border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
          >
            <option value="email">Email only</option>
            <option value="whatsapp">WhatsApp only</option>
            <option value="both">Email + WhatsApp</option>
          </select>
          <button
            onClick={sendReengagement}
            disabled={sendingReengage}
            data-testid="crm-send-reengagement"
            className="bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm px-3 py-1.5 rounded-sm flex items-center gap-1 disabled:opacity-60"
          >
            {sendingReengage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send re-engagement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: profile edit + credit limit */}
        <div className="space-y-4">
          <div className="bg-white border border-neutral-200 rounded-sm p-5">
            <h2 className="text-lg font-bold text-[#12263A] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#F46A25]" /> Profile</h2>
            <div className="space-y-3">
              <FormField label="Full name" value={form.name} onChange={v => setField('name', v)} testid="crm-field-name" />
              <div>
                <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase">Tier</label>
                <select
                  value={form.user_type}
                  onChange={e => setField('user_type', e.target.value)}
                  data-testid="crm-field-user-type"
                  className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
                >
                  <option value="end_user">End user</option>
                  <option value="builder">Builder</option>
                  <option value="retailer">Retailer</option>
                </select>
              </div>
              <FormField label="Company" icon={Building2} value={form.company_name} onChange={v => setField('company_name', v)} testid="crm-field-company" />
              <div className="grid grid-cols-2 gap-2">
                <FormField label="VAT no" icon={Hash} value={form.vat_no} onChange={v => setField('vat_no', v)} testid="crm-field-vat" />
                <FormField label="EORI no" icon={Hash} value={form.eori_no} onChange={v => setField('eori_no', v)} testid="crm-field-eori" />
              </div>
              <FormField label="Postcode" icon={MapPin} value={form.postcode} onChange={v => setField('postcode', v.toUpperCase())} testid="crm-field-postcode" />
              <FormField label="Address" icon={MapPin} value={form.address} onChange={v => setField('address', v)} testid="crm-field-address" />
              <div className="grid grid-cols-2 gap-2">
                <FormField label="Contact" icon={Phone} value={form.contact_no} onChange={v => setField('contact_no', v)} testid="crm-field-contact" />
                <FormField label="Mobile" icon={Smartphone} value={form.mobile_no} onChange={v => setField('mobile_no', v)} testid="crm-field-mobile" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase flex items-center gap-1"><Wallet className="w-3 h-3" /> Credit limit (£)</label>
                <input
                  type="number" min="0" step="0.01"
                  value={form.credit_limit}
                  onChange={e => setField('credit_limit', e.target.value)}
                  data-testid="crm-field-credit-limit"
                  className="w-full border border-neutral-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#F46A25]"
                />
              </div>
              <button
                onClick={saveProfile}
                disabled={saving}
                data-testid="crm-save-profile"
                className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold text-sm py-2 rounded-sm flex items-center justify-center gap-1 disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save profile
              </button>
            </div>
          </div>

          {/* Frequent products */}
          <div className="bg-white border border-neutral-200 rounded-sm p-5">
            <h2 className="text-lg font-bold text-[#12263A] mb-3">Frequently purchased</h2>
            {profile.frequent_products.length === 0 ? (
              <div className="text-sm text-neutral-500">No purchases yet.</div>
            ) : (
              <div className="space-y-2" data-testid="crm-frequent-products">
                {profile.frequent_products.map(fp => (
                  <div key={fp.product_id} className="flex items-start justify-between gap-2 text-sm border-b border-neutral-100 pb-2 last:border-0">
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${fp.product_id}`} className="font-semibold text-[#12263A] hover:text-[#F46A25] line-clamp-1">{fp.name}</Link>
                      {fp.sku && <div className="text-[10px] text-neutral-500 font-mono">{fp.sku}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs font-bold text-[#F46A25]">×{fp.qty}</div>
                      <div className="text-[10px] text-neutral-500">£{fp.total_spent.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Middle & right: analytics + orders */}
        <div className="space-y-4">
          {/* Monthly analytics */}
          <div className="bg-white border border-neutral-200 rounded-sm p-5">
            <h2 className="text-lg font-bold text-[#12263A] mb-3">Purchases by month</h2>
            {profile.monthly.length === 0 ? (
              <div className="text-sm text-neutral-500">No monthly data.</div>
            ) : (
              <MonthlyChart buckets={profile.monthly} />
            )}
          </div>
          <div className="bg-white border border-neutral-200 rounded-sm p-5">
            <h2 className="text-lg font-bold text-[#12263A] mb-3">Yearly totals</h2>
            {profile.yearly.length === 0 ? (
              <div className="text-sm text-neutral-500">No yearly data.</div>
            ) : (
              <table className="w-full text-sm" data-testid="crm-yearly-table">
                <thead className="text-xs uppercase text-neutral-500">
                  <tr><th className="text-left py-1">Year</th><th className="text-right py-1">Orders</th><th className="text-right py-1">Spend £</th></tr>
                </thead>
                <tbody>
                  {profile.yearly.map(y => (
                    <tr key={y.year} className="border-t border-neutral-100">
                      <td className="py-1 font-semibold">{y.year}</td>
                      <td className="py-1 text-right">{y.orders}</td>
                      <td className="py-1 text-right font-bold text-[#F46A25]">£{y.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Right: recent orders */}
        <div className="bg-white border border-neutral-200 rounded-sm p-5">
          <h2 className="text-lg font-bold text-[#12263A] mb-3">Recent orders</h2>
          {profile.recent_orders.length === 0 ? (
            <div className="text-sm text-neutral-500">No orders placed.</div>
          ) : (
            <div className="space-y-2" data-testid="crm-recent-orders">
              {profile.recent_orders.map(o => (
                <div key={o.id} className="border border-neutral-200 rounded-sm p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs font-mono text-[#12263A]">#{o.id.slice(0, 8)}</div>
                      <div className="text-[10px] text-neutral-500">{o.created_at ? new Date(o.created_at).toLocaleString('en-GB') : '—'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-[#F46A25]">£{Number(o.total).toFixed(2)}</div>
                      <div className={`text-[10px] uppercase font-bold ${STATUS_CLASS[o.status] || 'text-neutral-500'}`}>{o.status}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-neutral-600 mt-1">
                    {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? '' : 's'} · {o.payment_method}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pricing tree */}
      <div className="mt-6 bg-white border border-neutral-200 rounded-sm p-5">
        <h2 className="text-lg font-bold text-[#12263A] mb-1">Per-product pricing (Category tree)</h2>
        <p className="text-xs text-neutral-500 mb-4">
          Set a <strong>fixed price</strong>, a <strong>discount %</strong>, or a customer-specific
          <strong> bulk trigger qty + bulk price</strong> for any product. If the customer buys ≥
          Bulk Qty, they pay the Bulk price; otherwise the product's standard bulk pricing (if any)
          still applies. Precedence: product fixed price &gt; product discount % &gt; category discount % &gt; tier discount.
        </p>
        <CategoryCRMPricingTree
          customerId={c.id}
          initialOverrides={profile.overrides}
          onChanged={load}
        />
      </div>
    </div>
  );
};

const STATUS_CLASS = {
  pending: 'text-neutral-500',
  confirmed: 'text-blue-700',
  shipped: 'text-orange-700',
  delivered: 'text-green-700',
  cancelled: 'text-red-700',
};

const StatTile = ({ label, value, icon: Icon, hint, accent, testid }) => (
  <div className={`bg-white border rounded-sm p-3 ${accent ? 'border-[#F46A25]' : 'border-neutral-200'}`} data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-[10px] uppercase text-neutral-500 font-bold">{label}</div>
        <div className={`text-lg font-black ${accent ? 'text-[#F46A25]' : 'text-[#12263A]'}`}>{value}</div>
        {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
      </div>
      {Icon && <Icon className={`w-4 h-4 shrink-0 ${accent ? 'text-[#F46A25]' : 'text-neutral-400'}`} />}
    </div>
  </div>
);

const FormField = ({ label, icon: Icon, value, onChange, testid }) => (
  <div>
    <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase">{label}</label>
    <div className="relative">
      {Icon && <Icon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />}
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className={`w-full border border-neutral-300 rounded-sm py-2 pr-2 text-sm focus:outline-none focus:border-[#F46A25] ${Icon ? 'pl-8' : 'pl-2'}`}
      />
    </div>
  </div>
);

const MonthlyChart = ({ buckets }) => {
  const max = Math.max(...buckets.map(b => b.total), 1);
  return (
    <div className="space-y-1.5" data-testid="crm-monthly-chart">
      {buckets.map(b => (
        <div key={`${b.year}-${b.month}`} className="flex items-center gap-2 text-xs">
          <div className="w-16 shrink-0 font-mono text-neutral-600">{monthName(b.month)} {String(b.year).slice(-2)}</div>
          <div className="flex-1 bg-neutral-100 rounded-sm overflow-hidden h-4 relative">
            <div className="bg-[#F46A25] h-full" style={{ width: `${(b.total / max) * 100}%` }} />
          </div>
          <div className="w-24 text-right font-bold text-[#12263A]">£{b.total.toFixed(2)}</div>
          <div className="w-10 text-right text-neutral-500">×{b.orders}</div>
        </div>
      ))}
    </div>
  );
};

export default CustomerCRMProfile;
